/**
 * Route SEO — the single source of truth for per-route metadata.
 *
 * Three consumers read this file:
 *
 *   1. `App.js` / `Group.js` at runtime (via `SeoHelmet`), so document.head
 *      stays correct as React Router moves between routes.
 *   2. `scripts/prerender-seo.js` at build time, which stamps each route's
 *      title / description / canonical / OG tags into its own static HTML
 *      file. This is the part that matters for search: Googlebot indexes the
 *      pre-JavaScript HTML, and because every route used to be served the
 *      root index.html, /daily and /contact advertised the *homepage's*
 *      canonical URL and sat in Search Console as "Discovered - currently
 *      not indexed".
 *   3. The same script, to emit sitemap.xml — so a route can't be added here
 *      and forgotten there.
 *
 * Keep this module plain data: Node imports it directly during the build, so
 * it must not import React or contain JSX.
 */

import { LANGUAGES } from './languages.js';
import { QUESTIONS } from './questions.js';

export const SITE_ORIGIN = 'https://www.langshazam.com';

// Fallback for routes that don't declare their own `image`. The old default
// (new_logi_1200.png) was a literal logo on transparent white; in social
// previews it read as "the website itself". home-og.png is the redesigned
// homepage card and is a much better generic fallback.
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/home-og.png`;

// Marks the JSON-LD blocks that belong to a *route*, as opposed to the
// sitewide Organization/WebSite blocks hard-coded in index.html. The
// prerender writes this attribute and useSeo looks for it, which is what
// lets the runtime replace prerendered blocks instead of appending a
// duplicate copy of the same structured data.
export const JSON_LD_MARKER = 'data-seo-jsonld';

/** Absolute, trailing-slash-normalised URL for a route path. */
export function canonicalUrl(path) {
  if (!path) return null;
  return `${SITE_ORIGIN}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
}

/**
 * Every route we serve HTML for.
 *
 *   path      Canonical path, or null for routes whose URL is dynamic. A null
 *             path means the prerendered file omits <link rel="canonical">
 *             and og:url so crawlers fall back to the request URL.
 *   htmlFile  File the prerender writes into build/. Needs a matching rewrite
 *             in vercel.json; the prerender fails the build if one is missing.
 *   sitemap   Sitemap entry, or null to stay out of sitemap.xml.
 *             `lastmod: 'build'` stamps the build date — only correct for
 *             routes whose content genuinely changes on its own (i.e. the
 *             daily puzzle). Everything else carries the date its copy last
 *             actually changed, because a sitemap that claims every page
 *             changed today teaches Google to ignore our lastmod entirely.
 *   seo       Passed to SeoHelmet / the prerender. `path` is injected from
 *             the route, so it is never written twice.
 *
 * SEO copy is tuned to the queries we already rank for in Search Console
 * (notably "shazam for languages" and "audio language detector") plus the
 * ones with high impressions but zero clicks ("detect spoken language",
 * "language detector audio", "detect audio language").
 */
/**
 * One SEO page per detectable language: /languages/<slug>. Generated from
 * src/seo/languages.js so adding a language there adds its page, its
 * prerendered HTML, and its sitemap entry in one go. All 32 files are served
 * by a single `/languages/:slug` -> `/languages/:slug.html` rewrite.
 */
const LANGUAGE_ROUTES = LANGUAGES.map((lang) => {
  const path = `/languages/${lang.slug}`;
  const where = lang.countries.slice(0, 3).join(', ');
  const title = `Detect & Transcribe ${lang.name} Speech from Audio — Free ${lang.name} Language Detector | LangShazam`;
  const description =
    `Identify ${lang.name} (${lang.nativeName}) from live audio in seconds and get a real-time ${lang.name} transcript. ` +
    `State-of-the-art spoken language detection for ${lang.speakers} speakers in ${where}.`;
  return {
    id: `lang-${lang.slug}`,
    path,
    htmlFile: `languages/${lang.slug}.html`,
    rewrite: { source: '/languages/:slug', destination: '/languages/:slug.html' },
    sitemap: { changefreq: 'monthly', priority: '0.7', lastmod: '2026-09-19' },
    seo: {
      title,
      description,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'LangShazam', item: SITE_ORIGIN },
            { '@type': 'ListItem', position: 2, name: 'Supported Languages', item: `${SITE_ORIGIN}/languages` },
            { '@type': 'ListItem', position: 3, name: lang.name, item: `${SITE_ORIGIN}${path}` },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: `Can LangShazam detect ${lang.name} from audio?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `Yes. Tap the microphone, speak or play ${lang.name} audio, and LangShazam identifies it as ${lang.name} within a few seconds using a state-of-the-art real-time speech model that listens to the audio itself.`,
              },
            },
            {
              '@type': 'Question',
              name: `Does it transcribe ${lang.name} speech?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: `Yes. A live ${lang.name} transcript appears as you speak, written in ${lang.script} script, and is saved to your account with the recording.`,
              },
            },
            {
              '@type': 'Question',
              name: `Which languages is ${lang.name} most often confused with?`,
              acceptedAnswer: {
                '@type': 'Answer',
                text: lang.confusedWith.length
                  ? `${lang.name} is most often confused with ${lang.confusedWith.join(' and ')}. LangShazam separates them from the audio rather than from text, which is what makes the distinction reliable.`
                  : `${lang.name} is acoustically distinctive, and LangShazam usually identifies it from a single phrase.`,
              },
            },
          ],
        },
      ],
    },
  };
});

/**
 * Question landing pages: /what-language-is-this, /what-language-am-i-hearing.
 * Generated from src/seo/questions.js. Each has its own exact rewrite in
 * vercel.json, so no `rewrite` field.
 */
const QUESTION_ROUTES = QUESTIONS.map((page) => {
  const path = `/${page.slug}`;
  return {
    id: `q-${page.slug}`,
    path,
    htmlFile: `${page.slug}.html`,
    sitemap: { changefreq: 'monthly', priority: '0.8', lastmod: '2026-10-28' },
    seo: {
      title: page.title,
      description: page.description,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'LangShazam', item: SITE_ORIGIN },
            { '@type': 'ListItem', position: 2, name: page.question, item: `${SITE_ORIGIN}${path}` },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: page.question,
          description: page.answer,
          step: page.steps.map((step, i) => ({ '@type': 'HowToStep', position: i + 1, name: step.name, text: step.text })),
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [{ q: page.question, a: page.answer }, ...page.faq].map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        },
      ],
    },
  };
});

export const ROUTES = [
  {
    id: 'home',
    path: '/',
    htmlFile: 'index.html',
    sitemap: { changefreq: 'weekly', priority: '1.0', lastmod: '2026-09-19' },
    seo: {
      title: 'LangShazam — Shazam for Languages | Detect & Identify Spoken Languages from Audio',
      description:
        'LangShazam is the Shazam for languages: state-of-the-art spoken language detection. Tap once to identify any of 32 languages from live audio in seconds, with a real-time transcript. Free with an account.',
      // Dedicated home OG card. Replaces the old `new_logi_1200.png` (just the
      // brand globe, which read in social previews as "this app is a logo").
      // The new card communicates what the product actually is — see
      // og-source/home-og.svg for the source.
      image: `${SITE_ORIGIN}/home-og.png`,
      // WebApplication and FAQPage live here rather than in index.html because
      // they describe the homepage specifically — the prerender would
      // otherwise copy them onto /terms and /privacy, claiming those pages
      // are a FAQ. index.html keeps only the genuinely sitewide blocks
      // (Organization, WebSite).
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'LangShazam',
          alternateName: 'Shazam for Languages',
          url: SITE_ORIGIN,
          description:
            'State-of-the-art audio language detection. Tap, speak, and identify 32 languages in seconds with a live transcript. Free online audio language detector.',
          applicationCategory: 'UtilityApplication',
          applicationSubCategory: 'Language Detection',
          operatingSystem: 'Any',
          browserRequirements: 'Requires a modern browser with microphone access',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          featureList: [
            'State-of-the-art real-time spoken language detection from microphone audio',
            '32 supported languages, detected and transcribed',
            'Live speech-to-text transcript as you speak',
            'Detection history, transcripts and recordings saved to your account',
            'Langle — listen and guess the language',
            'Free with a LangShazam account',
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How to detect a spoken language from audio',
          description:
            'Identify what language someone is speaking using LangShazam — a free online audio language detector that works in any browser.',
          totalTime: 'PT10S',
          step: [
            { '@type': 'HowToStep', position: 1, name: 'Open LangShazam', text: 'Visit langshazam.com in any modern browser and sign in — it takes a few seconds with Google or email. No app to install.' },
            { '@type': 'HowToStep', position: 2, name: 'Tap the microphone', text: 'Press the mic button to grant audio permission and start streaming audio.' },
            { '@type': 'HowToStep', position: 3, name: 'Speak or play the audio', text: 'Talk for a few seconds, or hold your phone near a podcast, video, or conversation.' },
            { '@type': 'HowToStep', position: 4, name: 'Get the detected language', text: 'LangShazam returns the detected language within seconds and keeps transcribing live while you speak.' },
          ],
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How does LangShazam detect language from audio?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: "LangShazam streams your audio to AssemblyAI's Universal-3.6 Pro real-time speech model, which transcribes and identifies the spoken language natively across 32 languages, with a confidence score on every phrase.",
              },
            },
            {
              '@type': 'Question',
              name: 'Is it free to use?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — LangShazam is free. Create an account with Google or email, tap the mic, and start detecting. Your detections, transcripts and recordings are saved for you.',
              },
            },
            {
              '@type': 'Question',
              name: 'What languages can be detected?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'LangShazam supports 32 languages including English, Spanish, French, Arabic, Chinese, Hindi, Portuguese, Russian, Japanese, Korean, Turkish, and more.',
              },
            },
            {
              '@type': 'Question',
              name: 'How accurate is the language detection?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We are building state-of-the-art language detection. LangShazam listens to the audio itself with a real-time multilingual speech model, so after a few seconds of clear speech accuracy is very high, including for closely related languages.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I get a transcript of what was said?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — every session shows a live transcript as you speak, in the detected language\'s own script, and it is saved to your account.',
              },
            },
            {
              '@type': 'Question',
              name: 'Does it work on mobile?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Absolutely. LangShazam works in any modern browser — Safari, Chrome, Firefox — on both desktop and mobile devices. No app download needed.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is my audio data stored?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Audio is streamed to our servers for transcription and may be retained briefly for quality monitoring before being deleted. We do not sell, share, or use audio for advertising or profiling. See our Privacy Policy for details.',
              },
            },
          ],
        },
      ],
    },
  },
  {
    id: 'langle',
    path: '/langle',
    htmlFile: 'langle.html',
    // The only route that legitimately changes every day: a new puzzle drops
    // at midnight UTC, so lastmod tracks the build.
    sitemap: { changefreq: 'daily', priority: '0.95', lastmod: 'build' },
    seo: {
      title: 'Langle — Listen and guess the language | LangShazam',
      description:
        'A daily game: listen to a short clip and guess the language. Six tries. New puzzle every day.',
      // Dedicated link-preview card for /langle so when someone shares their
      // result the message doesn't render as a generic logo. Designed at the
      // OpenGraph standard 1200x630 with the brand, the value prop, and a
      // sample emoji grid so the recipient instantly understands the game.
      image: `${SITE_ORIGIN}/daily-og.png`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'Game',
          name: 'Langle',
          url: `${SITE_ORIGIN}/langle`,
          description: 'Daily audio language game: listen and guess the language.',
          genre: ['Puzzle', 'Educational', 'Word'],
          gamePlatform: 'Web Browser',
          applicationCategory: 'GameApplication',
        },
      ],
    },
  },
  {
    id: 'languages',
    path: '/languages',
    htmlFile: 'languages.html',
    sitemap: { changefreq: 'monthly', priority: '0.9', lastmod: '2026-09-19' },
    seo: {
      title: 'Supported Languages — Detect & Transcribe 32 Spoken Languages | LangShazam',
      description:
        'All 32 languages LangShazam can detect and transcribe from audio: English, Spanish, Mandarin, Cantonese, Hindi, Arabic, Portuguese, Japanese, Korean, Turkish, Afrikaans, Zulu and more, each with its own detection page.',
    },
  },
  {
    id: 'about',
    path: '/about',
    htmlFile: 'about.html',
    sitemap: { changefreq: 'monthly', priority: '0.8', lastmod: '2026-09-19' },
    seo: {
      title: 'About LangShazam — How the Shazam-for-Languages Tool Works',
      description:
        "How LangShazam identifies spoken languages from audio in real time, and why we are building state-of-the-art language detection. Built by Kaan Karakas on AssemblyAI's Universal-3.6 Pro streaming model.",
    },
  },
  {
    id: 'contact',
    path: '/contact',
    htmlFile: 'contact.html',
    sitemap: { changefreq: 'monthly', priority: '0.7', lastmod: '2026-04-26' },
    seo: {
      title: 'Contact LangShazam — Feedback, API Access & Partnerships',
      description:
        'Get in touch about LangShazam: bug reports, feature requests, custom language-detection integrations, or unlimited API access for your product.',
    },
  },
  {
    id: 'privacy',
    path: '/privacy',
    htmlFile: 'privacy.html',
    sitemap: { changefreq: 'yearly', priority: '0.4', lastmod: '2026-09-28' },
    seo: {
      title: 'Privacy Policy | LangShazam',
      description:
        'How LangShazam handles audio recordings, transcripts, and analytics. We do not sell your data, profile you, or use audio for advertising.',
    },
  },
  {
    id: 'terms',
    path: '/terms',
    htmlFile: 'terms.html',
    sitemap: { changefreq: 'yearly', priority: '0.4', lastmod: '2026-09-28' },
    seo: {
      title: 'Terms of Service | LangShazam',
      description:
        'Terms of service for using LangShazam, the free online audio language detector.',
    },
  },
  ...QUESTION_ROUTES,
  ...LANGUAGE_ROUTES,
  {
    // /group/<id>. One static file backs every group because ids are dynamic;
    // vercel.json rewrites /group/(.*) here so chat apps that don't run our
    // JavaScript still preview the leaderboard card instead of the homepage.
    // Personalising the card per group (real name, real roster) needs an edge
    // function calling the backend on every crawler hit — see VERCEL.md.
    //
    // Deliberately absent from the sitemap: group pages are private invites,
    // not content we want indexed.
    id: 'group',
    path: null,
    htmlFile: 'group.html',
    sitemap: null,
    seo: {
      title: 'Langle group leaderboard | LangShazam',
      description:
        "Play the daily audio language game with friends on Langle. Track who guesses today's spoken language fastest on this private group leaderboard.",
      image: `${SITE_ORIGIN}/group-og.png`,
    },
  },
];

/**
 * Route SEO keyed by id, with each route's `path` folded in — `SEO.langle`
 * spreads straight into <SeoHelmet>. Routes with a dynamic URL (group) get no
 * `path` and are expected to pass their own.
 */
export const SEO = Object.fromEntries(
  ROUTES.map((route) => [
    route.id,
    route.path ? { ...route.seo, path: route.path } : { ...route.seo },
  ]),
);
